import { useCallback } from "react";
import { useCurrentSession } from "@ats/src/context/CurrentSessionContext";
import { usePlanFeatures } from "@shared/queryHooks/useBilling";
import { useAllInvites } from "@shared/queryHooks/useInvite";
import { getModalDisplayName, isLegacyUnlimitedJobsPlan } from "@shared/lib/planHelpers";

interface PlanLimitsGateResult {
  shouldProceed: boolean;
  modalType?: "jobLimit" | "userLimit" | "downgradeBlocked";
  modalData?: {
    currentPlanName: string;
    targetPlanName: string;
    targetJobLimit?: number;
    targetUserLimit?: number | string;
    publishedJobsCount?: number;
    activeUserCount?: number;
    isActiveSubscription: boolean;
    isLegacyUnlimitedJobsPlan: boolean;
    hasPendingInvites?: boolean;
  };
}

export function usePlanLimitsGate(
  useExternalData: boolean = false,
  externalPlanFeaturesData?: any,
) {
  const { currentOrganization } = useCurrentSession();
  const { publishedJobsCount, plan, stripeSubscriptionInGoodStanding } = currentOrganization;

  // Only fetch if we're not using external data
  const { data: fetchedPlanFeaturesData } = usePlanFeatures({
    refetchOnWindowFocus: false,
    enabled: !useExternalData, // Disable query if using external data
  });

  const { data: invitesData } = useAllInvites();

  // Use external data if flag is true, otherwise use fetched data
  const planFeaturesData = useExternalData ? externalPlanFeaturesData : fetchedPlanFeaturesData;

  const checkPlanLimitsGate = useCallback(
    (targetPlanKey: string): PlanLimitsGateResult => {
      if (!planFeaturesData?.plans) {
        console.warn("Plan features data not available");
        return { shouldProceed: true };
      }

      const plans = planFeaturesData.plans;

      // Find target plan data
      const targetPlanData = plans.find((p) => p.plan === targetPlanKey);
      if (!targetPlanData) {
        console.warn("Could not find target plan data");
        return { shouldProceed: true };
      }

      console.log("usePlanLimitsGate - targetPlanData:", targetPlanData);

      // Find current plan data - only if subscription is in good standing
      const currentPlanData = stripeSubscriptionInGoodStanding
        ? plans.find((p) => p.plan === plan)
        : null;
      const currentPlanName = currentPlanData
        ? getModalDisplayName(currentPlanData.plan)
        : "Current";

      // Count active users (not deactivated) + pending invites
      const activeUserCount = currentOrganization.organizationUsers.filter(
        (orgUser) => orgUser.isActive,
      ).length;
      const pendingInviteCount = invitesData?.items
        ? invitesData.items.filter((invite) => invite.status === "status_pending").length
        : 0;
      const totalActiveUsers = activeUserCount + pendingInviteCount;
      const hasPendingInvites = pendingInviteCount > 0;

      console.log("usePlanLimitsGate - user counts:", {
        activeUserCount,
        pendingInviteCount,
        totalActiveUsers,
        hasPendingInvites,
      });

      // Check 1: Job limits (for upgrades)
      if (publishedJobsCount > targetPlanData.jobLimit) {
        console.log("usePlanLimitsGate - user has too many jobs, showing job limit modal");

        return {
          shouldProceed: false,
          modalType: "jobLimit",
          modalData: {
            currentPlanName,
            targetPlanName: getModalDisplayName(targetPlanData.plan),
            targetJobLimit: targetPlanData.jobLimit,
            publishedJobsCount,
            isActiveSubscription: stripeSubscriptionInGoodStanding,
            isLegacyUnlimitedJobsPlan: isLegacyUnlimitedJobsPlan(plan),
          },
        };
      }

      // Check 2: User limits (for both upgrades and downgrades)
      // Only check if target plan has a user limit (not unlimited)
      if (targetPlanData.userLimit !== "unlimited" && totalActiveUsers > targetPlanData.userLimit) {
        console.log(
          "usePlanLimitsGate - user has too many active users, showing user limit modal",
          {
            totalActiveUsers,
            targetUserLimit: targetPlanData.userLimit,
          },
        );

        return {
          shouldProceed: false,
          modalType: "userLimit",
          modalData: {
            currentPlanName,
            targetPlanName: getModalDisplayName(targetPlanData.plan),
            targetUserLimit: targetPlanData.userLimit,
            activeUserCount: totalActiveUsers,
            isActiveSubscription: stripeSubscriptionInGoodStanding,
            isLegacyUnlimitedJobsPlan: isLegacyUnlimitedJobsPlan(plan),
            hasPendingInvites,
          },
        };
      }

      console.log("usePlanLimitsGate - no limits exceeded, proceeding");
      // No limits exceeded, proceed
      return { shouldProceed: true };
    },
    [
      planFeaturesData,
      publishedJobsCount,
      plan,
      stripeSubscriptionInGoodStanding,
      currentOrganization,
      invitesData,
    ],
  );

  return {
    checkPlanLimitsGate,
  };
}
